import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { endpoints } from '@/shared/api/endpoints'

export type PixKeyType = 'CPF' | 'EMAIL' | 'PHONE' | 'EVP'
export type KycStatus = 'PENDING' | 'COMPLETE'

/** Owner-only personal data. CPF and Pix key are never returned in full — only masked. */
export type PersonalData = {
  fullName:       string | null
  birthDate:      string | null   // ISO date (YYYY-MM-DD)
  phone:          string | null
  uf:             string | null
  city:           string | null
  hasCpf:         boolean
  cpfMask:        string | null
  hasPixKey:      boolean
  pixKeyType:     PixKeyType | null
  pixKeyMask:     string | null
  pixKeyVerified: boolean
  kycStatus:      KycStatus
}

export type UpdatePersonalDataInput = {
  fullName:  string | null
  cpf:       string | null
  birthDate: string | null
  phone:     string | null
  uf:        string | null
  city:      string | null
}

export type UpdatePixKeyInput = { keyType: PixKeyType; keyValue: string }
export type PixKeyResult = { pixKeyType: PixKeyType; pixKeyMask: string; pixKeyVerified: boolean }

export const personalDataKeys = {
  all: ['personalData'] as const,
  me:  () => [...personalDataKeys.all, 'me'] as const,
}

export async function fetchMyPersonalData(): Promise<PersonalData> {
  return apiClient.get<PersonalData>(endpoints.users.personalData())
}

export function useMyPersonalData() {
  return useQuery({
    queryKey:  personalDataKeys.me(),
    queryFn:   fetchMyPersonalData,
    // Sensitive PII: do not keep it warm in the cache between visits.
    staleTime: 0,
    gcTime:    0,
  })
}

export function useUpdatePersonalData() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdatePersonalDataInput) =>
      apiClient.put<PersonalData>(endpoints.users.personalData(), input),
    onSuccess: (data) => {
      queryClient.setQueryData(personalDataKeys.me(), data)
    },
  })
}

export function useUpdatePixKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdatePixKeyInput) =>
      apiClient.put<PixKeyResult>(endpoints.users.pixKey(), input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: personalDataKeys.me() })
    },
  })
}
